import { SolText } from './sol-text';
import { SolIcon } from '@sol/icons';
import { SolColor, SolInkColor } from './sol-color';
import { ObjectTools } from '../../tools/object-tools';
import { SolSeverity } from './sol-severity';
import { SeverityTools } from '../../tools/severity-tools';
import { SolStatus, SolStatusType } from './sol-status';
import { SolTooltip } from './sol-tooltip';

export type SolTagSize = 'default' | 'large';

export type SolTagTextStyle = 'default' | 'bold';

export type SolTagStyle = 'default' | 'outline';

export type SolTagColor =
  // Categories
  | 'primary'
  | 'accent'
  | 'success'
  | 'warning'
  | 'danger'
  // Named colors
  | 'blue'
  | 'cyan'
  | 'green'
  | 'orange'
  | 'pink'
  | 'purple'
  | 'red'
  | 'tangerine'
  | 'violet'
  | 'yellow'
  | 'grey'
  | 'transparent';

export class SolTag {
  private constructor(
    public readonly icon: SolIcon | undefined,
    public readonly text: SolText,
    public readonly info: SolText | undefined,
    public readonly tooltip: SolTooltip | undefined,
    public readonly color: SolTagColor | undefined,
    public readonly iconColor: SolColor | undefined,
    public readonly textColor: SolInkColor | undefined,
    public readonly textStyle: SolTagTextStyle | undefined,
    public readonly style: SolTagStyle | undefined,
    public readonly backgroundColor: SolColor | undefined,
    public readonly size: SolTagSize | undefined,
    public readonly disabled: boolean,
    public readonly removable: boolean,
    public readonly tags: SolTag[],
    public readonly styleVars: Record<string, string>,
  ) {}

  public static empty(): SolTag {
    return SolTag.of(SolText.empty());
  }

  public static normalize(tag: SolTag | { text: string; color?: string; icon?: SolIcon; disabled?: boolean }): SolTag {
    return tag instanceof SolTag ? tag : SolTag.fromLegacy(tag);
  }

  public static normalizeArray(
    tags: Array<SolTag | { text: string; color?: string; icon?: SolIcon; disabled?: boolean }>,
  ): SolTag[] {
    return tags.map(tag => SolTag.normalize(tag));
  }

  public static fromLegacy(tag: { text: string; color?: string; icon?: SolIcon; disabled?: boolean }): SolTag {
    const color = SolTag.normalizeColor(tag.color);
    let result = SolTag.of(SolText.ofRaw(tag.text ?? '')).withColor(color);
    if (tag.icon !== undefined) {
      result = result.withIcon(tag.icon);
    }
    if (tag.disabled === true) {
      result = result.withDisabled(true);
    }
    return result;
  }

  public static fromDto(dto: any): SolTag {
    const base = SolTag.of(SolText.ofRaw(dto?.text?.text ?? dto?.text ?? ''));
    const withColor = dto?.color !== undefined ? base.withColor(SolTag.normalizeColor(dto?.color)) : base;
    const withInfo = dto?.info ? withColor.withInfo(SolText.ofRaw(dto.info?.text ?? dto.info)) : withColor;
    const withIcon = dto?.icon ? withInfo.withIcon(dto.icon as SolIcon) : withInfo;
    const withIconColor = dto?.iconColor ? withIcon.withIconColor(dto.iconColor) : withIcon;
    const withTextColor = dto?.textColor ? withIconColor.withTextColor(dto.textColor) : withIconColor;
    const withTextStyle = dto?.textStyle ? withTextColor.withTextStyle(dto.textStyle) : withTextColor;
    const withStyle = dto?.style ? withTextStyle.withStyle(dto.style) : withTextStyle;
    const withBackgroundColor =
      dto?.backgroundColor !== undefined ? withStyle.withBackgroundColor(dto.backgroundColor) : withStyle;
    const withSize = dto?.size ? withBackgroundColor.withSize(dto.size) : withBackgroundColor;
    const withDisabled = dto?.disabled ? withSize.withDisabled(true) : withSize;
    const withRemovable = dto?.removable ? withDisabled.withRemovable(true) : withDisabled;
    const childDtos = Array.isArray(dto?.tags) ? dto.tags : [];
    const childTags = SolTag.normalizeArray(
      childDtos.map((child: any) => (child instanceof SolTag ? child : SolTag.fromDto(child))),
    );
    return withRemovable.withTags(childTags);
  }

  public static of(text: SolText): SolTag {
    const tag = new SolTag(
      undefined,
      text,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      false,
      false,
      [],
      {},
    );
    return tag.recalculate();
  }

  public static ofStatus(status: SolStatus): SolTag {
    const icon = SolTag.toStatusIcon(status.type);
    const iconColor = SolTag.toStatusColor(status.type);
    return SolTag.of(status.text).withTextColor('ink').withTextStyle('bold').withIcon(icon).withIconColor(iconColor);
  }

  public static ofSeverity(severity: SolSeverity): SolTag {
    const text = SolText.ofRaw(severity.toLevelText());
    const info = SolText.ofRaw(severity.toText());
    const iconColor = SeverityTools.calculateColor(severity);
    return SolTag.of(text)
      .withInfo(info)
      .withTextColor('ink')
      .withTextStyle('bold')
      .withIcon(SolIcon.circle_filled_small)
      .withIconColor(iconColor)
      .withSize('large')
      .withTags(severity.tags);
  }

  public get hasText(): boolean {
    return !this.text.isEmpty();
  }

  public get hasInfo(): boolean {
    return this.info !== undefined && !this.info.isEmpty();
  }

  public get hasChildren(): boolean {
    return this.tags.length > 0;
  }

  public get isIconOnly(): boolean {
    return this.icon !== undefined && !this.hasText && !this.hasInfo && !this.hasChildren;
  }

  public get normalizedChildTags(): SolTag[] {
    return this.tags.map(tag => {
      let normalized = tag;
      if (tag.size !== 'default') {
        normalized = normalized.withSize('default');
      }
      if (tag.textStyle === undefined) {
        normalized = normalized.withTextStyle('default');
      }
      return normalized;
    });
  }

  public sizeOrDefault(): SolTagSize {
    return this.size ?? 'default';
  }

  public styleOrDefault(): SolTagStyle {
    return this.style ?? 'default';
  }

  public textStyleOrDefault(): SolTagTextStyle {
    return this.textStyle ?? 'default';
  }

  public withIcon(icon: SolIcon | undefined): SolTag {
    return this.copy({ icon }).calculateStyles();
  }

  public withText(text: SolText): SolTag {
    return this.copy({ text }).calculateStyles();
  }

  public withInfo(info: SolText | undefined): SolTag {
    return this.copy({ info }).calculateStyles();
  }

  public withTooltip(tooltip: SolTooltip | undefined): SolTag {
    return this.copy({ tooltip });
  }

  public withColor(color: SolTagColor | undefined): SolTag {
    return this.copy({ color }).calculateStyles();
  }

  public withIconColor(iconColor: SolColor | undefined): SolTag {
    return this.copy({ iconColor }).calculateStyles();
  }

  public withTextColor(textColor: SolInkColor | undefined): SolTag {
    return this.copy({ textColor }).calculateStyles();
  }

  public withTextStyle(textStyle: SolTagTextStyle | undefined): SolTag {
    return this.copy({ textStyle }).calculateStyles();
  }

  public withStyle(style: SolTagStyle | undefined): SolTag {
    return this.copy({ style }).calculateStyles();
  }

  public withBackgroundColor(backgroundColor: SolColor | undefined): SolTag {
    return this.copy({ backgroundColor }).calculateStyles();
  }

  public withSize(size: SolTagSize | undefined): SolTag {
    if (this.tags.length > 0 && size !== 'large') {
      return this;
    }
    return this.copy({ size }).calculateStyles();
  }

  public withDisabled(disabled: boolean): SolTag {
    return this.copy({ disabled }).calculateStyles();
  }

  public withRemovable(removable: boolean): SolTag {
    return this.copy({ removable }).calculateStyles();
  }

  public withTags(tags: SolTag[]): SolTag {
    const size = tags.length > 0 ? 'large' : this.size;
    tags = tags.map(tags => tags.withSize(undefined).withTags([]));
    return this.copy({ tags, size }).calculateStyles();
  }

  public toDto(): any {
    return {
      text: this.text,
      info: this.info,
      color: this.color ?? null,
      icon: this.icon,
      iconColor: this.iconColor,
      textColor: this.textColor,
      textStyle: this.textStyle,
      style: this.style,
      backgroundColor: this.backgroundColor,
      size: this.size,
      disabled: this.disabled,
      removable: this.removable,
      tags: this.tags.map(tag => tag.toDto()),
    };
  }

  private recalculate(): SolTag {
    return ObjectTools.copy<SolTag>(this, {
      styleVars: SolTag.calculateStyleVars(this),
    });
  }

  private calculateStyles(): SolTag {
    const styleVars = SolTag.calculateStyleVars(this);
    return this.copy({ styleVars });
  }

  private copy(props: Partial<SolTag>): SolTag {
    return ObjectTools.copy<SolTag>(this, props);
  }

  private static calculateStyleVars(tag: SolTag): Record<string, string> {
    const color = tag.color ?? 'grey';
    const isTransparent = color === 'transparent';
    const size = tag.sizeOrDefault();
    const style = tag.styleOrDefault();
    const textStyle = tag.textStyleOrDefault();
    const colorBase = SolTag.resolveBaseColor(color);
    const iconColorBase = SolTag.resolveIconColor(tag.iconColor, colorBase);
    const opacityBase = SolTag.resolveOpacityColor(color);
    const background = isTransparent ? 'transparent' : SolTag.resolveBackground(tag.backgroundColor, opacityBase);
    const textColor = SolTag.resolveTextColor(tag.textColor, colorBase, color);
    const borderColor = SolTag.resolveBorderColor(color, textColor);
    const bg = style === 'outline' ? 'transparent' : background;
    const border = style === 'outline' ? borderColor : 'transparent';
    const height = size === 'large' ? 'var(--sol-size-24)' : 'var(--sol-size-20)';
    const textWeight =
      textStyle === 'bold' ? 'var(--sol-text-body-small-bold-weight)' : 'var(--sol-text-body-small-weight)';
    return {
      '--sol-tag-bg': bg,
      '--sol-tag-border': border,
      '--sol-tag-text': textColor,
      '--sol-tag-icon': iconColorBase,
      '--sol-tag-text-weight': textWeight,
      '--sol-tag-height': height,
      opacity: tag.disabled ? '0.4' : '1',
    };
  }

  private static resolveBaseColor(color: SolTagColor): string {
    if (color === 'grey' || color === 'transparent') {
      return 'var(--sol-color-ink-opacity-high)';
    }
    return `var(--sol-color-${color})`;
  }

  private static resolveIconColor(iconColor: SolColor | undefined, fallback: string): string {
    if (iconColor === undefined) {
      return fallback;
    }
    return `var(--sol-color-${iconColor})`;
  }

  private static resolveOpacityColor(color: SolTagColor): string {
    if (color === 'grey' || color === 'transparent') {
      return 'var(--sol-color-opacity-low)';
    }
    return `var(--sol-color-${color}-opacity)`;
  }

  private static resolveBackground(background: SolColor | undefined, fallback: string): string {
    if (background === undefined) {
      return fallback;
    }
    if (background.startsWith('opacity')) {
      return `var(--sol-color-${background})`;
    }
    return `var(--sol-color-${background}-opacity)`;
  }

  private static resolveTextColor(textColor: SolInkColor | undefined, fallback: string, color: SolTagColor): string {
    if (textColor !== undefined) {
      return `var(--sol-color-${textColor})`;
    }
    if (color === 'grey' || color === 'transparent') {
      return 'var(--sol-color-ink-opacity-high)';
    }
    return fallback;
  }

  private static resolveBorderColor(color: SolTagColor, textColor: string): string {
    if (color === 'grey' || color === 'transparent') {
      return 'var(--sol-color-opacity-mid)';
    }
    return textColor;
  }

  private static normalizeColor(color: string | undefined): SolTagColor | undefined {
    if (color === undefined) {
      return undefined;
    }
    const allowed: SolTagColor[] = [
      'primary',
      'accent',
      'success',
      'warning',
      'danger',
      'blue',
      'cyan',
      'green',
      'orange',
      'pink',
      'purple',
      'red',
      'tangerine',
      'violet',
      'yellow',
      'grey',
      'transparent',
    ];
    return allowed.includes(color as SolTagColor) ? (color as SolTagColor) : 'grey';
  }

  private static toStatusIcon(statusType: SolStatusType): SolIcon {
    switch (statusType) {
      case 'positive':
        return SolIcon.circle_filled_small;
      case 'negative':
        return SolIcon.circle_ban_small;
      case 'pending':
        return SolIcon.circle_dashed_small;
    }
  }

  private static toStatusColor(statusType: SolStatusType): SolColor {
    switch (statusType) {
      case 'positive':
        return 'success';
      case 'negative':
        return 'danger';
      case 'pending':
        return 'blue';
    }
  }
}
